import NotifikasiPush from './NotifikasiPush';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { Alert, Box, Button, Chip, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import LayoutKasir from './layoutKasir';
import { Logout, reset } from '../../fitur/AuthSlice';
import { getApiBaseUrl } from './productpercabang/apiProductPercabang';
import { labelAntrian } from '../../utils/antrian';
import { adaSuaraIndonesia, panggilSuara, suaraTersedia } from '../../utils/suaraAntrian';

const jam = (iso) => new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

/**
 * Antrian hari ini untuk kasir: daftar nomor antrian + tombol "Panggil" yang mengucapkan nomor dengan suara otomatis
 * (Web Speech API) lalu mencatat panggilan ke server. Diperbarui otomatis tiap 10 detik.
 */
const AntrianKasir = ({ modeDapur = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [galat, setGalat] = useState('');
  const [hanyaBelum, setHanyaBelum] = useState(false);
  const [suaraId, setSuaraId] = useState(true);
  const [terakhirDipanggil, setTerakhirDipanggil] = useState(null);
  const [peringatan, setPeringatan] = useState('');

  const muat = useCallback(async () => {
    try {
      const r = await axios.get(`${getApiBaseUrl()}/antrian`, { withCredentials: true });
      setData(r.data?.data || []);
      setGalat('');
    } catch (e) {
      setGalat(e?.response?.data?.message || 'Gagal memuat antrian. Periksa koneksi ke server.');
    }
  }, []);

  useEffect(() => {
    muat();
    const t = setInterval(muat, 10000);
    return () => clearInterval(t);
  }, [muat]);

  // Daftar suara kadang baru tersedia beberapa saat setelah halaman dibuka.
  useEffect(() => {
    if (!suaraTersedia()) return undefined;
    const cek = () => setSuaraId(adaSuaraIndonesia());
    cek();
    window.speechSynthesis.addEventListener('voiceschanged', cek);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', cek);
  }, []);

  const panggil = async (item) => {
    // Pesanan yang belum dibayar tidak bisa dipanggil (kasir menagih dulu).
    if (!item.lunas) {
      setPeringatan(`Nomor ${labelAntrian(item)} belum dibayar. Selesaikan pembayaran dulu sebelum memanggil.`);
      return;
    }
    setPeringatan('');
    panggilSuara({ nomor: item.nomorAntrian, awalan: item.awalanAntrian, nama: item.namaCustomer });
    setTerakhirDipanggil(labelAntrian(item));
    try {
      await axios.post(`${getApiBaseUrl()}/antrian/${item.uuid}/panggil`, {}, { withCredentials: true });
      muat();
    } catch (_) {
      // Suara tetap berbunyi walau pencatatan gagal (mis. server sibuk).
    }
  };

  // Belum dipanggil di atas (terbaru dulu), yang sudah dipanggil di bawah.
  const tampil = useMemo(() => {
    const daftar = hanyaBelum ? data.filter((x) => !x.dipanggilAt) : data;
    return [...daftar].sort((a, b) => Number(!!a.dipanggilAt) - Number(!!b.dipanggilAt) || new Date(b.dibuat) - new Date(a.dibuat));
  }, [data, hanyaBelum]);

  const isi = (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Antrian Hari Ini</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel control={<Switch checked={hanyaBelum} onChange={(e) => setHanyaBelum(e.target.checked)} />} label="Belum dipanggil saja" />
            <Button
              variant="outlined"
              startIcon={<CampaignOutlined />}
              onClick={() => panggilSuara({ nomor: 1, nama: '' }, { ulang: 1 })}
              disabled={!suaraTersedia()}
            >
              Tes suara
            </Button>
          </Box>
        </Box>
        <NotifikasiPush sx={{ mb: 1, display: { xs: 'block', md: 'none' } }} />
        {!suaraTersedia() && <Alert severity="warning" sx={{ mb: 2 }}>Browser ini tidak mendukung suara otomatis. Pakai Chrome atau Edge.</Alert>}
        {suaraTersedia() && !suaraId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Suara Bahasa Indonesia belum terpasang di perangkat ini; suara bawaan dipakai (pelafalan bisa kurang pas). Di Windows: Pengaturan &gt; Waktu &amp; bahasa &gt; Suara.
          </Alert>
        )}
        {galat && <Alert severity="error" sx={{ mb: 2 }}>{galat}</Alert>}
        {peringatan && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPeringatan('')}>{peringatan}</Alert>}
        {terakhirDipanggil && <Alert severity="success" sx={{ mb: 2 }}>Memanggil nomor {terakhirDipanggil}…</Alert>}
        {tampil.length === 0 && !galat && <Typography color="text.secondary">Belum ada antrian{hanyaBelum ? ' yang menunggu dipanggil' : ' hari ini'}.</Typography>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {tampil.map((item) => (
            <Paper key={item.uuid} sx={{ p: 2, borderRadius: 3, border: item.dipanggilAt ? '1px solid #bbb' : item.lunas ? '2px solid #337ab7' : '2px solid #ed6c02', opacity: item.dipanggilAt ? 0.85 : 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 44, fontWeight: 900, lineHeight: 1, color: '#337ab7' }}>{labelAntrian(item)}</Typography>
                <Button
                  variant={item.lunas ? 'contained' : 'outlined'}
                  startIcon={<CampaignOutlined />}
                  onClick={() => panggil(item)}
                  sx={item.lunas ? { bgcolor: '#337ab7' } : { color: '#9e9e9e', borderColor: '#bdbdbd' }}
                >
                  {item.lunas ? 'Panggil' : 'Belum bayar'}
                </Button>
              </Box>
              <Typography sx={{ mt: 1, fontWeight: 700 }}>{item.namaCustomer || 'Customer'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {jam(item.dibuat)} · {item.metodePesanan === 'dinein' ? `Dine in${item.namaMeja ? ` · Meja ${item.namaMeja}` : ''}` : `Take away${item.takeawayName ? ` · ${item.takeawayName}` : ''}`}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip size="small" color={item.sumber === 'qr' ? 'info' : 'default'} label={item.sumber === 'qr' ? 'Pesan QR' : 'Kasir'} />
                <Chip size="small" color={item.lunas ? 'success' : 'warning'} label={item.lunas ? 'Lunas' : 'Belum dibayar'} />
                {item.dipanggilAt ? (
                  <Chip size="small" variant="outlined" label={`Dipanggil ${item.jumlahPanggil}× · ${jam(item.dipanggilAt)}`} />
                ) : (
                  <Chip size="small" variant="outlined" label="Belum dipanggil" />
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
  );

  // Akun dapur: halaman sendiri tanpa menu kasir (hanya lihat antrian & panggil), tombol kembali ke layar dapur.
  if (modeDapur) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#F7F8FA' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, background: '#fff', borderBottom: '1px solid #E8E8E8', position: 'sticky', top: 0, zIndex: 10 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dapur')} sx={{ color: '#1a1a1a', fontWeight: 700 }}>Kembali ke Dapur</Button>
        </Box>
        {isi}
      </Box>
    );
  }

  return (
    <LayoutKasir onNavigate={(p) => navigate(p)} onLogout={() => { dispatch(reset()); dispatch(Logout()); navigate('/'); }} hideLayoutMeja>
      {isi}
    </LayoutKasir>
  );
};

export default AntrianKasir;
